import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["appearance", "ariaLiveMessages", "components", "isInvalid", "onClickPreventDefault", "spacing", "styles", "tabSelectsValue", "validationState"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
// eslint-disable-next-line @repo/internal/fs/filename-pattern-match
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { mergeStyles } from 'react-select';
import { ClearIndicator, DropdownIndicator, IndicatorSeparator, LoadingIndicator, MultiValueRemove } from './components';
import { Input } from './components/input-aria-describedby';
import { NoOptionsMessage } from './components/no-options';
import baseStyles from './styles';
import { isOptionsGrouped, onFocus as _onFocus } from './utils/grouped-options-announcement';
export default function createSelect(WrappedComponent) {
  var AtlaskitSelect = /*#__PURE__*/forwardRef(function AtlaskitSelect(props, forwardedRef) {
    var appearance = props.appearance,
      ariaLiveMessages = props.ariaLiveMessages,
      componentsProp = props.components,
      isInvalid = props.isInvalid,
      _props$onClickPrevent = props.onClickPreventDefault,
      onClickPreventDefault = _props$onClickPrevent === void 0 ? true : _props$onClickPrevent,
      _props$spacing = props.spacing,
      spacing = _props$spacing === void 0 ? 'default' : _props$spacing,
      _props$styles = props.styles,
      styles = _props$styles === void 0 ? {} : _props$styles,
      _props$tabSelectsValu = props.tabSelectsValue,
      tabSelectsValue = _props$tabSelectsValu === void 0 ? false : _props$tabSelectsValu,
      _props$validationStat = props.validationState,
      validationState = _props$validationStat === void 0 ? 'default' : _props$validationStat,
      restProps = _objectWithoutProperties(props, _excluded);
    var internalSelectRef = useRef(null);
    var components = useMemo(function () {
      return _objectSpread({
        ClearIndicator: ClearIndicator,
        DropdownIndicator: DropdownIndicator,
        LoadingIndicator: LoadingIndicator,
        MultiValueRemove: MultiValueRemove,
        IndicatorSeparator: IndicatorSeparator,
        Input: Input,
        NoOptionsMessage: NoOptionsMessage
      }, componentsProp);
    }, [componentsProp]);
    var descriptionId = props['aria-describedby'];
    var isSearchable = props.isSearchable;
    useEffect(function () {
      if (!isSearchable && descriptionId) {
        var _internalSelectRef$cu;
        // when isSearchable is false, react-select will create its own dummy input instead of using ours,
        // so we need to manually add the additional aria-describedby using ref.
        var input = (_internalSelectRef$cu = internalSelectRef.current) === null || _internalSelectRef$cu === void 0 ? void 0 : _internalSelectRef$cu.inputRef;
        var ariaDescribedby = input === null || input === void 0 ? void 0 : input.getAttribute('aria-describedby');
        if (!(ariaDescribedby !== null && ariaDescribedby !== void 0 && ariaDescribedby.includes(descriptionId))) {
          input === null || input === void 0 || input.setAttribute('aria-describedby', "".concat(ariaDescribedby, " ").concat(descriptionId));
        }
      }
    }, [descriptionId, isSearchable]);
    var isCompact = spacing === 'compact';

    /**
     * The following `useImperativeHandle` hook exists for the sake of backwards compatibility.
     * This component used to be a class component which set the value of the `ref` prop to object with the properties and value as below.
     * This has lead to slightly odd usage of refs with this component, e.g. `myRef.current.select.select.controlRef` instead of just `myRef.current.select.controlRef`
     * In the next major release, this should removed and the ref should be passed directly to the wrapped component (given users have updated usage)
     * More info https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/pull-requests/88021/overview
     */
    useImperativeHandle(forwardedRef, function () {
      return {
        select: internalSelectRef.current,
        focus: function focus() {
          var _internalSelectRef$cu2;
          return (_internalSelectRef$cu2 = internalSelectRef.current) === null || _internalSelectRef$cu2 === void 0 ? void 0 : _internalSelectRef$cu2.focus();
        },
        blur: function blur() {
          var _internalSelectRef$cu3;
          return (_internalSelectRef$cu3 = internalSelectRef.current) === null || _internalSelectRef$cu3 === void 0 ? void 0 : _internalSelectRef$cu3.blur();
        }
      };
    }, []);
    return /*#__PURE__*/React.createElement(WrappedComponent, _extends({
      ref: internalSelectRef,
      "aria-live": "assertive",
      ariaLiveMessages: isOptionsGrouped(props.options) ? _objectSpread({
        onFocus: function onFocus(data) {
          return _onFocus(data, props.options);
        }
      }, ariaLiveMessages) : _objectSpread({}, ariaLiveMessages),
      tabSelectsValue: tabSelectsValue,
      onClickPreventDefault: onClickPreventDefault
    }, restProps, {
      components: components
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
      ,
      styles: mergeStyles(
      // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
      baseStyles(
      // This will cover both props for invalid state while giving priority to isInvalid. When cleaning up validationState, we can just keep the inner condition.
      typeof isInvalid !== 'undefined' ? isInvalid ? 'error' : 'default' : validationState, isCompact, appearance || 'default'), styles)
    }));
  });
  AtlaskitSelect.displayName = 'AtlaskitSelect';
  return AtlaskitSelect;
}